import axios, { type AxiosInstance } from "axios"
import config from "config"
import type {
  IAnalyticsAdoption,
  IAnalyticsExamDuration,
  IAnalyticsExamFailReason,
  IAnalyticsExamSafetyLesson,
  IAnalyticsExamsEquipment,
  IAnalyticsExamsLesson,
  IAnalyticsExamsSummary,
  IAnalyticsExamTaskLesson,
  IAnalyticsFailedExam,
  IAnalyticsLicenseOrg,
  IAnalyticsTrainingUserDetail,
  IAnalyticsTrainingOrg,
  IAnalyticsExamRetake,
  IAnalyticsUserLicense,
} from "types/analyticsTypes"
import authService from "./authService"

class AnalyticsService {
  private http: AxiosInstance

  public constructor() {
    this.http = axios.create({
      baseURL: config.apiUrl,
      headers: {
        "Content-type": "application/json",
      },
    })
  }

  private authHeaders = async () => {
    const accessToken = await authService.getAccessToken()
    return { headers: { Authorization: `Bearer ${accessToken}` } }
  }

  public getLicenses = async (): Promise<IAnalyticsLicenseOrg[]> => {
    const cfg = await this.authHeaders()
    const { data } = await this.http.get<IAnalyticsLicenseOrg[]>("/analytics/licenses", cfg)
    return data
  }

  public getTraining = async (period = 90): Promise<IAnalyticsTrainingOrg[]> => {
    const cfg = await this.authHeaders()
    const { data } = await this.http.get<IAnalyticsTrainingOrg[]>(
      `/analytics/training?period=${period}`,
      cfg,
    )
    return data
  }

  public getExamsSummary = async (orgId?: string | null): Promise<IAnalyticsExamsSummary> => {
    const cfg = await this.authHeaders()
    const url = orgId ? `/analytics/exams/summary?orgId=${orgId}` : "/analytics/exams/summary"
    const { data } = await this.http.get<IAnalyticsExamsSummary>(url, cfg)
    return data
  }

  public getExamsLessons = async (orgId?: string | null): Promise<IAnalyticsExamsLesson[]> => {
    const cfg = await this.authHeaders()
    const url = orgId ? `/analytics/exams/lessons?orgId=${orgId}` : "/analytics/exams/lessons"
    const { data } = await this.http.get<IAnalyticsExamsLesson[]>(url, cfg)
    return data
  }

  public getExamsEquipment = async (orgId?: string | null): Promise<IAnalyticsExamsEquipment[]> => {
    const cfg = await this.authHeaders()
    const url = orgId ? `/analytics/exams/equipment?orgId=${orgId}` : "/analytics/exams/equipment"
    const { data } = await this.http.get<IAnalyticsExamsEquipment[]>(url, cfg)
    return data
  }

  public getExamsDuration = async (orgId?: string | null, status?: string | null): Promise<IAnalyticsExamDuration[]> => {
    const cfg = await this.authHeaders()
    const params = new URLSearchParams()
    if (orgId) params.set("orgId", orgId)
    if (status) params.set("status", status)
    const query = params.toString()
    const url = `/analytics/exams/duration${query ? `?${query}` : ""}`
    const { data } = await this.http.get<IAnalyticsExamDuration[]>(url, cfg)
    return data
  }

  public getExamsTasks = async (orgId?: string | null): Promise<IAnalyticsExamTaskLesson[]> => {
    const cfg = await this.authHeaders()
    const url = orgId ? `/analytics/exams/tasks?orgId=${orgId}` : "/analytics/exams/tasks"
    const { data } = await this.http.get<IAnalyticsExamTaskLesson[]>(url, cfg)
    return data
  }

  public getExamsSafety = async (orgId?: string | null): Promise<IAnalyticsExamSafetyLesson[]> => {
    const cfg = await this.authHeaders()
    const url = orgId ? `/analytics/exams/safety?orgId=${orgId}` : "/analytics/exams/safety"
    const { data } = await this.http.get<IAnalyticsExamSafetyLesson[]>(url, cfg)
    return data
  }

  public getExamsFailed = async (orgId?: string, period?: number): Promise<IAnalyticsFailedExam[]> => {
    const cfg = await this.authHeaders()
    const params = new URLSearchParams()
    if (orgId) params.set("orgId", orgId)
    if (period !== undefined) params.set("period", String(period))
    const query = params.toString()
    const url = query ? `/analytics/exams/failed?${query}` : "/analytics/exams/failed"
    const { data } = await this.http.get<IAnalyticsFailedExam[]>(url, cfg)
    return data
  }

  public getExamsFailReasons = async (orgId?: string): Promise<IAnalyticsExamFailReason[]> => {
    const cfg = await this.authHeaders()
    const url = orgId ? `/analytics/exams/failreasons?orgId=${orgId}` : "/analytics/exams/failreasons"
    const { data } = await this.http.get<IAnalyticsExamFailReason[]>(url, cfg)
    return data
  }

  public getAdoption = async (orgId?: string): Promise<IAnalyticsAdoption> => {
    const cfg = await this.authHeaders()
    const url = orgId ? `/analytics/adoption?orgId=${orgId}` : "/analytics/adoption"
    const { data } = await this.http.get<IAnalyticsAdoption>(url, cfg)
    return data
  }

  public getTrainingDetail = async (orgId: string, period = 90): Promise<IAnalyticsTrainingUserDetail[]> => {
    const cfg = await this.authHeaders()
    const { data } = await this.http.get<IAnalyticsTrainingUserDetail[]>(
      `/analytics/training/detail?orgId=${orgId}&period=${period}`,
      cfg,
    )
    return data
  }

  public getExamRetakes = async (orgId?: string | null): Promise<IAnalyticsExamRetake[]> => {
    const cfg = await this.authHeaders()
    const url = orgId ? `/analytics/exams/retakes?orgId=${orgId}` : "/analytics/exams/retakes"
    const { data } = await this.http.get<IAnalyticsExamRetake[]>(url, cfg)
    return data
  }

  public getTrainingUserLicenses = async (orgId: string): Promise<IAnalyticsUserLicense[]> => {
    const cfg = await this.authHeaders()
    const { data } = await this.http.get<IAnalyticsUserLicense[]>(
      `/analytics/training/licenses?orgId=${orgId}`,
      cfg,
    )
    return data
  }

  public getTrainingUserLicensesForUser = async (orgId: string, userId: string): Promise<IAnalyticsUserLicense[]> => {
    const cfg = await this.authHeaders()
    const { data } = await this.http.get<IAnalyticsUserLicense[]>(
      `/analytics/training/licenses/user?orgId=${orgId}&userId=${userId}`,
      cfg,
    )
    return data
  }
}

const analyticsService = new AnalyticsService()

export default analyticsService
